/**
 * Utility functions for formatting order data
 */

export interface Address {
  city: string;
  street: string;
}

/**
 * Format date to Russian locale
 */
export const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Format address for display
 */
export const formatAddress = (address: Address | string | undefined): string => {
  if (!address) return '';
  if (typeof address === 'string') return address;
  return `${address.city}, ${address.street}`;
};

/**
 * Format price with currency
 */
export const formatPrice = (price: number | string | null | undefined): string => {
  // Handle different data types safely
  if (price === null || price === undefined) {
    return '0.00 ₽';
  }
  
  // Convert to number if it's a string
  const numericPrice = typeof price === 'string' ? Number.parseFloat(price) : Number(price);
  
  // Check if it's a valid number
  if (Number.isNaN(numericPrice)) {
    return '0.00 ₽';
  }
  
  return `${numericPrice.toFixed(2)} ₽`;
};

/**
 * Format order number with hash
 */
export const formatOrderNumber = (orderId: number | string): string => {
  return `#${orderId}`;
};

/**
 * Get order type display text
 */
export const getOrderTypeText = (orderType: string): string => {
  const typeMap: Record<string, string> = {
    'delivery': 'Доставка',
    'takeaway': 'Самовывоз',
    'dine_in': 'Ем в зале',
  };
  return typeMap[orderType] || orderType;
};

/**
 * Get payment method display text
 */
export const getPaymentMethodText = (paymentMethod: string): string => {
  const methodMap: Record<string, string> = {
    'card': 'Карта',
    'cash': 'Наличные',
    'online': 'Онлайн',
  };
  return methodMap[paymentMethod] || paymentMethod;
};

/**
 * Get payment URL from different sources
 * @param order - order object
 * @param modalData - modal data object
 * @returns payment URL or null
 */
export const getPaymentUrl = (order: any, modalData: any): string | null => {
  return (
    order?.paymentUrl ||
    order?.payment_url ||
    order?.confirmationUrl ||
    order?.confirmation_url ||
    order?.payment?.paymentUrl ||
    order?.payment?.payment_url ||
    order?.payment?.confirmationUrl ||
    order?.payment?.confirmation_url ||
    modalData?.paymentUrl ||
    modalData?.payment_url ||
    modalData?.confirmationUrl ||
    modalData?.confirmation_url ||
    modalData?.order?.paymentUrl ||
    modalData?.order?.payment_url ||
    modalData?.order?.confirmationUrl ||
    modalData?.order?.confirmation_url ||
    null
  );
};

/**
 * Calculate order total from items
 * @param items - array of order items
 * @returns total amount
 */
export const calculateOrderTotal = (items: any[]): number => {
  if (!Array.isArray(items)) return 0;
  
  return items.reduce((sum: number, item: any) => {
    const itemTotal = Number(item.totalPrice || item.total_price || 0);
    // Handle NaN values
    return sum + (Number.isNaN(itemTotal) ? 0 : itemTotal);
  }, 0);
};

/**
 * Format order date (legacy function name)
 */
export const formatOrderDate = (dateString: string): string => {
  return formatDate(dateString);
};

/**
 * Format payment expiration date
 */
export const formatPaymentExpiresAt = (expiresAtString: string): string => {
  if (!expiresAtString) return '';
  
  const date = new Date(expiresAtString);
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Check if payment URL is expired
 */
export const isPaymentExpired = (order: any): boolean => {
  if (order?.payment_url_expired !== undefined) {
    return order.payment_url_expired;
  }
  
  // Fallback: check by expiration time
  if (order?.payment_expires_at) {
    return new Date() > new Date(order.payment_expires_at);
  }
  
  return false;
};